import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { paymentsAPI } from "../lib/api";
import { toast } from "sonner";
import {
  Link2,
  Copy,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  MessageCircle,
} from "lucide-react";
import { cn } from "../lib/utils";

const TYPE_OPTIONS = [
  { value: "deposit", label: "Anticipo sugerido", hint: "20% del total (calculado automático)" },
  { value: "anticipo_libre", label: "Anticipo libre", hint: "Monto manual, sin restricción de %" },
  { value: "parcial", label: "Pago parcial", hint: "Monto manual" },
  { value: "balance", label: "Saldo total", hint: "Cubre todo lo pendiente" },
];

const fmtMoney = (n, currency = "MXN") => {
  const v = Number(n || 0);
  return `$${v.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
};

export function PaymentLinkDialog({ open, onOpenChange, contractId, onLinkCreated }) {
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [type, setType] = useState("deposit");
  const [manualAmount, setManualAmount] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);

  const fetchSummary = useCallback(async () => {
    if (!contractId) return;
    setLoadingSummary(true);
    try {
      const res = await paymentsAPI.getPaymentSummary(contractId);
      setSummary(res.data);
      // Default to deposit if remaining > 0, else parcial
      if (res.data.deposit_remaining > 0 && res.data.deposit_remaining <= res.data.balance) {
        setType("deposit");
      } else {
        setType("parcial");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar el resumen del contrato");
      onOpenChange(false);
    } finally {
      setLoadingSummary(false);
    }
  }, [contractId, onOpenChange]);

  useEffect(() => {
    if (open) {
      setCreatedLink(null);
      setManualAmount("");
      fetchSummary();
    }
  }, [open, fetchSummary]);

  // Calculate effective amount based on selected type
  const effectiveAmount = (() => {
    if (!summary) return 0;
    if (type === "deposit") return Math.min(summary.deposit_remaining, summary.balance);
    if (type === "balance") return summary.balance;
    const m = parseFloat(manualAmount);
    return isNaN(m) ? 0 : m;
  })();

  const exceedsBalance = summary && effectiveAmount > summary.balance + 0.01;
  const isInvalidAmount = !summary || effectiveAmount <= 0 || exceedsBalance;

  const handleCreate = async () => {
    if (!summary) return;
    if (isInvalidAmount) return;
    setCreating(true);
    try {
      const payload = {
        contract_id: contractId,
        payment_type: type,
        custom_amount: (type === "anticipo_libre" || type === "parcial") ? effectiveAmount : null,
      };
      const res = await paymentsAPI.createStripeCheckout(payload);
      setCreatedLink({
        url: res.data.url,
        amount: res.data.amount,
        currency: res.data.currency,
        description: res.data.description,
        session_id: res.data.session_id,
      });
      toast.success("Liga de pago generada");
      if (typeof onLinkCreated === "function") onLinkCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear la liga");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async () => {
    if (!createdLink?.url) return;
    try {
      await navigator.clipboard.writeText(createdLink.url);
      toast.success("Liga copiada al portapapeles");
    } catch {
      toast.error("No se pudo copiar; copia manual desde el campo");
    }
  };

  const sendByWhatsApp = () => {
    if (!createdLink || !summary) return;
    const msg =
      `Hola${summary.contract_id ? "" : ""}, le compartimos la liga de pago seguro:\n\n` +
      `Concepto: ${createdLink.description}\n` +
      `Monto: ${fmtMoney(createdLink.amount, createdLink.currency)}\n\n` +
      `${createdLink.url}\n\n` +
      `La liga vence en ${summary.link_ttl_hours} horas.`;
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="payment-link-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Link2 className="w-5 h-5 text-blue-600" />
            Generar liga de pago
          </DialogTitle>
        </DialogHeader>

        {loadingSummary || !summary ? (
          <div className="py-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
          </div>
        ) : !summary.user_can_generate ? (
          <div className="p-4 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
            Tu rol no puede generar ligas para este contrato.
          </div>
        ) : !summary.can_create_link ? (
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-700 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            El contrato ya está completamente pagado. No es necesario generar una liga.
          </div>
        ) : createdLink ? (
          <div className="space-y-3" data-testid="payment-link-success">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
              <div className="flex items-center gap-2 text-emerald-700 font-medium mb-1">
                <CheckCircle2 className="w-4 h-4" />
                Liga creada
              </div>
              <div className="text-xs text-slate-700 space-y-0.5">
                <div>Concepto: <strong>{createdLink.description}</strong></div>
                <div>Monto: <strong>{fmtMoney(createdLink.amount, createdLink.currency)}</strong></div>
                <div>Vence en {summary.link_ttl_hours} h</div>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">URL de pago</Label>
              <Input value={createdLink.url} readOnly className="font-mono text-xs" data-testid="generated-link-input" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={copyLink} variant="outline" data-testid="copy-link-btn">
                <Copy className="w-4 h-4 mr-1" /> Copiar link
              </Button>
              <Button onClick={sendByWhatsApp} variant="outline" className="border-emerald-300 text-emerald-700" data-testid="whatsapp-link-btn">
                <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
              </Button>
              <a href={createdLink.url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" data-testid="open-link-btn">
                  <ExternalLink className="w-4 h-4 mr-1" /> Abrir
                </Button>
              </a>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="payment-link-form">
            {/* Summary panel */}
            <div className="bg-slate-50 rounded border p-3 text-sm grid grid-cols-2 gap-2">
              <div>
                <div className="text-[11px] text-slate-500">Total contrato</div>
                <div className="font-semibold">{fmtMoney(summary.total_amount, summary.currency)}</div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500">Total pagado</div>
                <div className="font-semibold text-emerald-700">{fmtMoney(summary.total_paid, summary.currency)}</div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500">Saldo pendiente</div>
                <div className="font-semibold text-amber-700">{fmtMoney(summary.balance, summary.currency)}</div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500">Monto liga</div>
                <div className={cn("font-semibold", exceedsBalance ? "text-rose-700" : "text-blue-700")}>
                  {fmtMoney(effectiveAmount, summary.currency)}
                </div>
              </div>
            </div>

            {summary.active_pending_links?.length > 0 && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  Hay {summary.active_pending_links.length} liga(s) pendiente(s) sin pagar para este contrato. Puedes generar otra; el cliente podrá pagar cualquiera.
                </span>
              </div>
            )}

            {/* Type selector */}
            <div>
              <Label className="text-xs text-slate-700">Tipo de liga</Label>
              <RadioGroup value={type} onValueChange={setType} className="mt-2 space-y-2">
                {TYPE_OPTIONS.map((opt) => {
                  const disabled = opt.value === "deposit" && summary.deposit_remaining <= 0;
                  return (
                    <label
                      key={opt.value}
                      className={cn(
                        "flex items-start gap-2 p-2 rounded border cursor-pointer hover:bg-slate-50",
                        type === opt.value && "border-blue-300 bg-blue-50/50",
                        disabled && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <RadioGroupItem value={opt.value} id={`type-${opt.value}`} disabled={disabled} data-testid={`type-${opt.value}`} />
                      <div className="text-sm flex-1">
                        <div className="font-medium">{opt.label}</div>
                        <div className="text-[11px] text-slate-500">{opt.hint}</div>
                        {opt.value === "deposit" && (
                          <div className="text-[11px] text-slate-600">
                            Sugerido: {fmtMoney(summary.deposit_remaining, summary.currency)}
                            {summary.deposit_remaining <= 0 && <Badge className="ml-1 h-4 text-[9px] bg-emerald-100 text-emerald-700">Cubierto</Badge>}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>
            </div>

            {/* Manual amount input */}
            {(type === "anticipo_libre" || type === "parcial") && (
              <div>
                <Label className="text-xs text-slate-700">Monto manual</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={summary.balance}
                  value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                  placeholder={`Máx. ${fmtMoney(summary.balance, summary.currency)}`}
                  className={cn("mt-1", exceedsBalance && "border-rose-400 focus:border-rose-500")}
                  data-testid="manual-amount-input"
                />
                {exceedsBalance && (
                  <p className="text-[11px] text-rose-600 mt-1">El monto excede el saldo pendiente</p>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {createdLink ? (
            <Button onClick={() => onOpenChange(false)} data-testid="close-link-dialog-btn">Cerrar</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              {summary?.user_can_generate && summary?.can_create_link && (
                <Button
                  onClick={handleCreate}
                  disabled={isInvalidAmount || creating}
                  data-testid="create-link-btn"
                >
                  {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
                  Generar liga
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
